import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Shield, Plus, Pencil, Trash2, Search, AlertTriangle, Building2, Map, List } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ConfirmDeleteDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS_CONTROL = [
  'Administrativo',
  'Ingeniería',
  'EPP',
  'Señalización',
  'Capacitación',
  'Procedimiento',
  'Otro'
];

// Badge de control para vista mapa
function ControlBadge({ control, tipo, searchTerm = '' }) {
  const colors = {
    'Administrativo': 'bg-blue-100 text-blue-700',
    'Ingeniería': 'bg-purple-100 text-purple-700',
    'EPP': 'bg-amber-100 text-amber-700',
    'Señalización': 'bg-cyan-100 text-cyan-700',
    'Capacitación': 'bg-emerald-100 text-emerald-700',
    'Procedimiento': 'bg-indigo-100 text-indigo-700',
  };
  const colorClass = colors[tipo] || 'bg-slate-100 text-slate-700';
  
  // Resaltar si coincide con búsqueda
  const isMatch = searchTerm && (
    control.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tipo.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className={`flex items-center gap-2 border rounded-lg px-3 py-2 transition-colors ${
      isMatch 
        ? 'bg-yellow-50 border-yellow-300 ring-2 ring-yellow-200' 
        : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
    }`}>
      <Shield className="w-4 h-4 text-emerald-600" />
      <span className="text-sm font-medium text-emerald-800">
        <HighlightText text={control} search={searchTerm} />
      </span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded ${colorClass}`}>
        <HighlightText text={tipo} search={searchTerm} />
      </span>
    </div>
  );
}

// Función para resaltar texto en búsqueda
function HighlightText({ text, search }) {
  if (!search || !text) return <span>{text}</span>;
  
  const parts = text.split(new RegExp(`(${search})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) => 
        part.toLowerCase() === search.toLowerCase() 
          ? <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
          : part
      )}
    </span>
  );
}

// Tarjeta de riesgo para vista mapa
function RiesgoCard({ riesgo, controlesList, searchTerm = '' }) {
  const isRiesgoMatch = searchTerm && (
    riesgo.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (riesgo.descripcion && riesgo.descripcion.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className={`bg-white rounded-lg border overflow-hidden ${
      isRiesgoMatch ? 'border-yellow-300 ring-2 ring-yellow-200' : 'border-slate-200'
    }`}>
      <div className="bg-gradient-to-r from-red-50 to-teal-50 px-4 py-3 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-slate-900">
              <HighlightText text={riesgo.nombre} search={searchTerm} />
            </h4>
            {riesgo.descripcion && (
              <p className="text-xs text-slate-500 line-clamp-1">
                <HighlightText text={riesgo.descripcion} search={searchTerm} />
              </p>
            )}
          </div>
          <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
            {controlesList.length} control(es)
          </span>
        </div>
      </div>
      <div className="p-3">
        {controlesList.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-2">Sin controles asociados</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {controlesList.map(c => (
              <ControlBadge key={c.id} control={c.nombre} tipo={c.tipo} searchTerm={searchTerm} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Sección de área para vista mapa
function AreaMapSection({ area, riesgosList, controles, searchTerm = '' }) {
  const getControlesForRiesgo = (riesgoId) => {
    return controles.filter(c => c.riesgo_id === riesgoId);
  };

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3">
        <div className="flex items-center gap-2 text-white">
          <Building2 className="w-5 h-5" />
          <h3 className="font-semibold text-lg">
            <HighlightText text={area.nombre} search={searchTerm} />
          </h3>
          <span className="ml-auto text-sm text-blue-100">
            {riesgosList.length} riesgo(s)
          </span>
        </div>
      </div>
      <div className="p-4 space-y-4 bg-slate-50">
        {riesgosList.map(riesgo => (
          <RiesgoCard 
            key={riesgo.id} 
            riesgo={riesgo} 
            controlesList={getControlesForRiesgo(riesgo.id)}
            searchTerm={searchTerm}
          />
        ))}
      </div>
    </div>
  );
}

export const Controles = () => {
  const { isAdmin } = useAuth();
  const [controles, setControles] = useState([]);
  const [riesgos, setRiesgos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('riesgos');
  
  // Filtros
  const [searchControl, setSearchControl] = useState('');
  const [searchRiesgo, setSearchRiesgo] = useState('');
  const [searchMapa, setSearchMapa] = useState('');
  const [filterAreaId, setFilterAreaId] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  
  // Dialogs
  const [isControlDialogOpen, setIsControlDialogOpen] = useState(false);
  const [isRiesgoDialogOpen, setIsRiesgoDialogOpen] = useState(false);
  const [editingControl, setEditingControl] = useState(null);
  const [editingRiesgo, setEditingRiesgo] = useState(null);
  
  // Forms
  const [controlForm, setControlForm] = useState({ nombre: '', tipo: '', riesgo_id: '', area_id: '' });
  const [riesgoForm, setRiesgoForm] = useState({ nombre: '', descripcion: '', area_id: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, itemId: null, tabla: '', deps: [], loadingDeps: false });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [controlesRes, riesgosRes, areasRes] = await Promise.all([
        axios.get(`${API}/controles`),
        axios.get(`${API}/riesgos`),
        axios.get(`${API}/areas`)
      ]);
      setControles(controlesRes.data);
      setRiesgos(riesgosRes.data);
      setAreas(areasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Helpers
  const getRiesgoAreaId = (riesgoId) => {
    const riesgo = riesgos.find(r => r.id === riesgoId);
    return riesgo?.area_id || null;
  };

  const getRiesgoName = (riesgoId) => {
    const riesgo = riesgos.find(r => r.id === riesgoId);
    return riesgo?.nombre || '-';
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.id === areaId);
    return area?.nombre || '-';
  };

  const getAreaNameByRiesgo = (riesgoId) => {
    const areaId = getRiesgoAreaId(riesgoId);
    return areaId ? getAreaName(areaId) : '-';
  };

  const getTipoBadgeColor = (tipo) => {
    const colors = {
      'Administrativo': 'bg-blue-100 text-blue-700',
      'Ingeniería': 'bg-purple-100 text-purple-700',
      'EPP': 'bg-amber-100 text-amber-700',
      'Señalización': 'bg-cyan-100 text-cyan-700',
      'Capacitación': 'bg-emerald-100 text-emerald-700',
      'Procedimiento': 'bg-indigo-100 text-indigo-700',
    };
    return colors[tipo] || 'bg-slate-100 text-slate-700';
  };

  // Filtros
  const getFilteredControles = () => {
    return controles.filter(control => {
      const matchesSearch = control.nombre.toLowerCase().includes(searchControl.toLowerCase());
      const matchesArea = filterAreaId === 'all' || getRiesgoAreaId(control.riesgo_id) === filterAreaId;
      return matchesSearch && matchesArea;
    });
  };

  const getFilteredRiesgos = () => {
    return riesgos.filter(riesgo => {
      const matchesSearch = riesgo.nombre.toLowerCase().includes(searchRiesgo.toLowerCase());
      const matchesArea = filterAreaId === 'all' || riesgo.area_id === filterAreaId;
      return matchesSearch && matchesArea;
    });
  };

  const getRiesgosForArea = (areaId) => riesgos.filter(r => r.area_id === areaId);
  
  const getRiesgosForForm = () => {
    if (!controlForm.area_id || controlForm.area_id === 'all') return riesgos;
    return riesgos.filter(r => r.area_id === controlForm.area_id);
  };

  const getAreasWithRiesgos = () => {
    const areasToCheck = filterAreaId === 'all' ? areas : areas.filter(a => a.id === filterAreaId);
    return areasToCheck
      .map(area => ({ area, riesgos: getRiesgosForArea(area.id) }))
      .filter(item => item.riesgos.length > 0);
  };

  // CRUD Riesgos
  const openRiesgoDialog = (riesgo = null) => {
    if (riesgo) {
      setEditingRiesgo(riesgo);
      setRiesgoForm({ nombre: riesgo.nombre, descripcion: riesgo.descripcion || '', area_id: riesgo.area_id });
    } else {
      setEditingRiesgo(null);
      setRiesgoForm({ nombre: '', descripcion: '', area_id: filterAreaId !== 'all' ? filterAreaId : '' });
    }
    setIsRiesgoDialogOpen(true);
  };

  const handleRiesgoSubmit = async (e) => {
    e.preventDefault();
    if (!riesgoForm.nombre || !riesgoForm.area_id) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      if (editingRiesgo) {
        await axios.put(`${API}/riesgos/${editingRiesgo.id}`, riesgoForm);
        toast.success('Riesgo actualizado');
      } else {
        await axios.post(`${API}/riesgos`, riesgoForm);
        toast.success('Riesgo creado');
      }
      setIsRiesgoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteRiesgo = async (id) => {
    setDeleteDialog({ open: true, itemId: id, tabla: 'riesgos', deps: [], loadingDeps: true });
    try {
      const res = await axios.get(`${API}/dependencias/riesgos/${id}`);
      setDeleteDialog(prev => ({ ...prev, deps: res.data.dependencias, loadingDeps: false }));
    } catch { setDeleteDialog(prev => ({ ...prev, loadingDeps: false })); }
  };

  // CRUD Controles
  const openControlDialog = (control = null) => {
    if (control) {
      setEditingControl(control);
      const areaId = getRiesgoAreaId(control.riesgo_id) || '';
      setControlForm({ nombre: control.nombre, tipo: control.tipo, riesgo_id: control.riesgo_id, area_id: areaId });
    } else {
      setEditingControl(null);
      setControlForm({ nombre: '', tipo: '', riesgo_id: '', area_id: filterAreaId !== 'all' ? filterAreaId : '' });
    }
    setIsControlDialogOpen(true);
  };

  const handleControlSubmit = async (e) => {
    e.preventDefault();
    if (!controlForm.nombre || !controlForm.tipo || !controlForm.riesgo_id) {
      toast.error('Completa los campos requeridos');
      return;
    }
    try {
      const payload = { nombre: controlForm.nombre, tipo: controlForm.tipo, riesgo_id: controlForm.riesgo_id };
      if (editingControl) {
        await axios.put(`${API}/controles/${editingControl.id}`, payload);
        toast.success('Control actualizado');
      } else {
        await axios.post(`${API}/controles`, payload);
        toast.success('Control creado');
      }
      setIsControlDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteControl = async (id) => {
    setDeleteDialog({ open: true, itemId: id, tabla: 'controles', deps: [], loadingDeps: false });
  };

  const confirmDeleteItem = async () => {
    const { itemId, tabla } = deleteDialog;
    try {
      await axios.delete(`${API}/${tabla}/${itemId}`);
      toast.success(tabla === 'riesgos' ? 'Riesgo eliminado' : 'Control eliminado');
      setDeleteDialog({ open: false, itemId: null, tabla: '', deps: [], loadingDeps: false });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
      setDeleteDialog({ open: false, itemId: null, tabla: '', deps: [], loadingDeps: false });
    }
  };

  // Contadores para badges
  const getControlesCount = (riesgoId) => controles.filter(c => c.riesgo_id === riesgoId).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const filteredControles = getFilteredControles();
  const filteredRiesgos = getFilteredRiesgos();
  const areasWithRiesgos = getAreasWithRiesgos();

  return (
    <div className="space-y-6" data-testid="controles-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Gestión de Riesgos y Controles</h1>
          <p className="text-slate-500">Administra riesgos, controles y sus asociaciones por área</p>
        </div>
      </div>

      {/* Filtro de Área global */}
      <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-slate-200 shadow-sm">
        <Building2 className="w-5 h-5 text-slate-400" />
        <span className="text-sm font-medium text-slate-700">Filtrar por Área:</span>
        <Select value={filterAreaId} onValueChange={setFilterAreaId}>
          <SelectTrigger className="w-[250px]" data-testid="filter-area-global">
            <SelectValue placeholder="Todas las áreas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las áreas</SelectItem>
            {areas.map(area => (
              <SelectItem key={area.id} value={area.id}>{area.nombre}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3 text-sm text-slate-500">
          <span className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            {filteredRiesgos.length} Riesgos
          </span>
          <span className="flex items-center gap-1">
            <Shield className="w-4 h-4 text-emerald-400" />
            {filteredControles.length} Controles
          </span>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="riesgos" className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Riesgos
          </TabsTrigger>
          <TabsTrigger value="controles" className="flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Controles
          </TabsTrigger>
          <TabsTrigger value="mapa" className="flex items-center gap-2">
            <Map className="w-4 h-4" />
            Vista Mapa
          </TabsTrigger>
        </TabsList>


        {/* Tab Controles */}
        <TabsContent value="controles" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar control..."
                    value={searchControl}
                    onChange={(e) => setSearchControl(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {isAdmin() && (
                  <Button onClick={() => openControlDialog()} className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Control
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Área</TableHead>
                    <TableHead>Riesgo</TableHead>
                    <TableHead>Control</TableHead>
                    <TableHead>Tipo</TableHead>
                    {isAdmin() && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredControles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No hay controles registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredControles.map(control => (
                      <TableRow key={control.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Building2 className="w-4 h-4 text-blue-400" />
                            {getAreaNameByRiesgo(control.riesgo_id)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-600">
                            <AlertTriangle className="w-4 h-4 text-red-400" />
                            {getRiesgoName(control.riesgo_id)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                              <Shield className="w-4 h-4 text-emerald-600" />
                            </div>
                            {control.nombre}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTipoBadgeColor(control.tipo)}`}>
                            {control.tipo}
                          </span>
                        </TableCell>
                        {isAdmin() && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openControlDialog(control)}>
                                <Pencil className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteControl(control.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Riesgos */}
        <TabsContent value="riesgos" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar riesgo..."
                    value={searchRiesgo}
                    onChange={(e) => setSearchRiesgo(e.target.value)}
                    className="pl-9"
                  />
                </div>
                {isAdmin() && (
                  <Button onClick={() => openRiesgoDialog()} className="bg-red-500 hover:bg-red-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Riesgo
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Área</TableHead>
                    <TableHead>Riesgo</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Controles</TableHead>
                    {isAdmin() && <TableHead className="text-right">Acciones</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRiesgos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-slate-500 py-8">
                        No hay riesgos registrados
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRiesgos.map(riesgo => (
                      <TableRow key={riesgo.id}>
                        <TableCell>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Building2 className="w-4 h-4 text-blue-400" />
                            {getAreaName(riesgo.area_id)}
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                              <AlertTriangle className="w-4 h-4 text-red-600" />
                            </div>
                            {riesgo.nombre}
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-500 max-w-xs truncate">
                          {riesgo.descripcion || '-'}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700">
                            {getControlesCount(riesgo.id)} control(es)
                          </span>
                        </TableCell>
                        {isAdmin() && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button variant="ghost" size="icon" onClick={() => openRiesgoDialog(riesgo)}>
                                <Pencil className="w-4 h-4 text-slate-500" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteRiesgo(riesgo.id)}>
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Vista Mapa */}
        <TabsContent value="mapa" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <CardTitle className="flex items-center gap-2">
                  <Map className="w-5 h-5 text-teal-500" />
                  Mapa de Riesgos y Controles por Área
                </CardTitle>
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar área, riesgo, control o tipo..."
                    value={searchMapa}
                    onChange={(e) => setSearchMapa(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                const search = searchMapa.toLowerCase().trim();
                
                // Filtrar datos según búsqueda
                const filteredAreasMap = areasWithRiesgos.map(item => {
                  if (!search) return item;
                  
                  // Filtrar riesgos que coincidan con la búsqueda
                  const matchedRiesgos = item.riesgos.filter(riesgo => {
                    const riesgoControles = controles.filter(c => c.riesgo_id === riesgo.id);
                    
                    // Buscar en área
                    const matchArea = item.area.nombre.toLowerCase().includes(search);
                    // Buscar en riesgo
                    const matchRiesgo = riesgo.nombre.toLowerCase().includes(search) || 
                                       (riesgo.descripcion && riesgo.descripcion.toLowerCase().includes(search));
                    // Buscar en controles
                    const matchControl = riesgoControles.some(c => 
                      c.nombre.toLowerCase().includes(search) || 
                      c.tipo.toLowerCase().includes(search)
                    );
                    
                    return matchArea || matchRiesgo || matchControl;
                  });
                  
                  return { ...item, riesgos: matchedRiesgos };
                }).filter(item => item.riesgos.length > 0);

                if (filteredAreasMap.length === 0) {
                  return (
                    <p className="text-center text-slate-500 py-8">
                      {search ? `No se encontraron resultados para "${searchMapa}"` : 'No hay datos para mostrar'}
                    </p>
                  );
                }

                return (
                  <div className="space-y-6">
                    {filteredAreasMap.map(item => (
                      <AreaMapSection 
                        key={item.area.id} 
                        area={item.area} 
                        riesgosList={item.riesgos}
                        controles={controles}
                        searchTerm={search}
                      />
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Control */}
      <Dialog open={isControlDialogOpen} onOpenChange={setIsControlDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingControl ? 'Editar Control' : 'Nuevo Control'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleControlSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del Control *</Label>
              <Input
                value={controlForm.nombre}
                onChange={(e) => setControlForm({ ...controlForm, nombre: e.target.value })}
                placeholder="Ej: Uso de EPP obligatorio"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Tipo de Control *</Label>
              <Select
                value={controlForm.tipo}
                onValueChange={(value) => setControlForm({ ...controlForm, tipo: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_CONTROL.map(tipo => (
                    <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Área (para filtrar riesgos)</Label>
              <Select
                value={controlForm.area_id}
                onValueChange={(value) => setControlForm({ ...controlForm, area_id: value, riesgo_id: '' })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar área" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las áreas</SelectItem>
                  {areas.map(area => (
                    <SelectItem key={area.id} value={area.id}>{area.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Riesgo Asociado *</Label>
              <Select
                value={controlForm.riesgo_id}
                onValueChange={(value) => setControlForm({ ...controlForm, riesgo_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar riesgo" />
                </SelectTrigger>
                <SelectContent>
                  {getRiesgosForForm().map(riesgo => (
                    <SelectItem key={riesgo.id} value={riesgo.id}>
                      {riesgo.nombre} ({getAreaName(riesgo.area_id)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsControlDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-teal-500 hover:bg-teal-600">
                {editingControl ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Riesgo */}
      <Dialog open={isRiesgoDialogOpen} onOpenChange={setIsRiesgoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRiesgo ? 'Editar Riesgo' : 'Nuevo Riesgo'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRiesgoSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Área *</Label>
              <Select
                value={riesgoForm.area_id}
                onValueChange={(value) => setRiesgoForm({ ...riesgoForm, area_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar área" />
                </SelectTrigger>
                <SelectContent>
                  {areas.map(area => (
                    <SelectItem key={area.id} value={area.id}>{area.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre del Riesgo *</Label>
              <Input
                value={riesgoForm.nombre}
                onChange={(e) => setRiesgoForm({ ...riesgoForm, nombre: e.target.value })}
                placeholder="Ej: Caída de altura"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={riesgoForm.descripcion}
                onChange={(e) => setRiesgoForm({ ...riesgoForm, descripcion: e.target.value })}
                placeholder="Descripción del riesgo..."
                rows={3}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsRiesgoDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-red-500 hover:bg-red-600">
                {editingRiesgo ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onOpenChange={(v) => !v && setDeleteDialog({ open: false, itemId: null, tabla: '', deps: [], loadingDeps: false })}
        onConfirm={confirmDeleteItem}
        title={deleteDialog.tabla === 'riesgos' ? 'Eliminar Riesgo' : 'Eliminar Control'}
        description={deleteDialog.tabla === 'riesgos' 
          ? '¿Esta seguro de eliminar este riesgo? Se eliminaran todos sus controles asociados.'
          : '¿Esta seguro de eliminar este control?'}
        dependencies={deleteDialog.deps}
        loadingDeps={deleteDialog.loadingDeps}
      />
    </div>
  );
};